/**
 * Logo Component
 * Secure, configurable logo component with fallback support
 */

import React, { useState, useEffect } from 'react';
import { getLogoPath, assetConfig } from '../../config/assets';
import './Logo.css';

const Logo = ({
    className = '',
    alt = 'AFF Logo',
    width,
    height,
    style = {},
    showText = false,
    text = 'AFF',
    textClassName = '',
    fallbackIcon = '🏛️',
    onError = null,
    onLoad = null,
    ...props
}) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [logoPath, setLogoPath] = useState(null);

    // Get logo path on component mount
    useEffect(() => {
        const path = getLogoPath();
        setLogoPath(path);

        // Log configuration in development
        if (process.env.NODE_ENV === 'development') {
            console.log('Logo component initialized with path:', path);
        }
    }, []);

    // Handle image load error
    const handleImageError = (event) => {
        console.warn('Logo image failed to load:', event.target.src);
        setImageError(true);

        if (onError) {
            onError(event);
        }
    };

    // Handle image load success
    const handleImageLoad = (event) => {
        setImageLoaded(true);

        if (onLoad) {
            onLoad(event);
        }
    };

    // If image failed to load or no valid path, show fallback
    if (imageError || !logoPath || logoPath === assetConfig.logo.fallback) {
        return (
            <div
                className={`logo-fallback ${className}`}
                style={{
                    width: width || '32px',
                    height: height || '32px',
                    fontSize: width ? `${parseInt(width) * 0.6}px` : '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    ...style
                }}
                {...props}
            >
                <span className="fallback-icon">{fallbackIcon}</span>
                {showText && (
                    <span className={`logo-text ${textClassName}`}>
                        {text}
                    </span>
                )}
            </div>
        );
    }

    // Render image logo
    return (
        <div className={`logo-container ${className}`} style={style}>
            <img
                src={logoPath}
                alt={alt}
                width={width}
                height={height}
                className={`logo-image ${imageLoaded ? 'loaded' : 'loading'}`}
                onError={handleImageError}
                onLoad={handleImageLoad}
                {...props}
            />
            {showText && (
                <span className={`logo-text ${textClassName}`}>
                    {text}
                </span>
            )}
        </div>
    );
};

export default Logo;
